import React, { useState, useMemo, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { QRCodeSVG } from 'qrcode.react';
import {
  BILAN_TYPES, BILAN_HABITUDES, BILAN_REGLES, BILAN_FICHES, RDV_PREALABLE_INFO, getBilanType, getBilanCompetences,
  getBilanTotal, computeBilanVerdict, buildBilanTexte, buildBilanTexteCompact, countText,
} from '../data/bilanReprise';
import {
  CheckCircle2, AlertTriangle, XCircle, ClipboardCheck, Trophy, RotateCcw, Printer,
  QrCode, Send, Copy, Loader2, X, Share2, MessageCircle, Mail, Car, Lock, ClipboardList,
  Maximize2, HelpCircle, Presentation, ChevronDown, Radio, Flag,
} from 'lucide-react';
import { PREREQUIS_LIST } from '../data/coursEnVoiture';
import { PREREQUIS_EXTRA } from '../data/coursEnVoitureQcm';
import { VISUAL_MAP } from './PrerequisVisuals';
import PrerequisPresentMode from './PrerequisPresentMode';
import RdvPrealableGuide from './RdvPrealableGuide';
import BilanPrintSheet from './BilanPrintSheet';
import { useSunMode } from './SunModeToggle';
import { Sun, Moon } from 'lucide-react';

const FICHES_BY_SLUG = Object.fromEntries(
  PREREQUIS_LIST.map((p) => [p.slug, { ...p, ...(PREREQUIS_EXTRA[p.slug] || {}) }])
);

const STATES = [
  { v: 'acquis',     l: 'Acquis',        cls: 'bg-emerald-600 text-white border-emerald-500', icon: CheckCircle2 },
  { v: 'consolider', l: 'À consolider',  cls: 'bg-amber-500 text-amber-950 border-amber-400', icon: AlertTriangle },
  { v: 'non_acquis', l: 'Non acquis',    cls: 'bg-red-600 text-white border-red-500',         icon: XCircle },
];

const VERDICT_STYLE = {
  pret:     { color: 'border-emerald-700/40 text-emerald-300 from-emerald-700/20', Icon: Trophy },
  presque:  { color: 'border-sky-700/40 text-sky-300 from-sky-700/20',             Icon: AlertTriangle },
  pas_pret: { color: 'border-amber-700/40 text-amber-300 from-amber-700/20',       Icon: XCircle },
  pending:  { color: 'border-white/10 text-white/60',                              Icon: ClipboardCheck },
};

const buzz = (pattern) => { try { navigator.vibrate?.(pattern); } catch { /* non supporté (iOS) */ } };

const LIVE_KEY = 'cv_bilan_live_v1';
const todayIso = () => new Date().toISOString().slice(0, 10);
const frDate = (d) => {
  if (!d) return '';
  if (d.includes('/')) return d;
  return new Date(`${d}T00:00:00`).toLocaleDateString('fr-FR');
};
const loadLiveSaved = () => { try { return JSON.parse(localStorage.getItem(LIVE_KEY) || 'null'); } catch { return null; } };

export default function BilanRepriseTab() {
  const { user } = useAuth();
  const isTeacher = user && user.role === 'enseignant';
  const saved = useMemo(loadLiveSaved, []);
  const [typeId, setTypeId] = useState(() => {
    const t = new URLSearchParams(window.location.search).get('type');
    if (BILAN_TYPES.some((b) => b.id === t)) return t;
    if (saved?.session && BILAN_TYPES.some((b) => b.id === saved.typeId)) return saved.typeId;
    return 'retour_aac';
  });
  const [grid, setGrid] = useState(() => (saved?.session && saved.grid) || {});
  const [habitudes, setHabitudes] = useState(() => (saved?.session && saved.habitudes) || {});
  const [meta, setMeta] = useState(() => {
    const base = (saved?.session && saved.meta) || { eleve: '', date: todayIso(), km: '', duree: '', rdv: '' };
    const paramEleve = new URLSearchParams(window.location.search).get('eleve');
    return paramEleve ? { ...base, eleve: paramEleve } : base;
  });
  const [conseils, setConseils] = useState(() => (saved?.session && saved.conseils) || '');
  const [resetConfirm, setResetConfirm] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareStatus, setShareStatus] = useState('idle');
  const [shareResult, setShareResult] = useState(null);
  const [internalSend, setInternalSend] = useState({ status: 'idle', msg: '' });
  const [shareError, setShareError] = useState('');
  const [copied, setCopied] = useState(false);
  const [textCopied, setTextCopied] = useState(null);
  const [presenting, setPresenting] = useState(null);
  const [live, setLive] = useState(() => saved?.session || null);
  const [liveOpen, setLiveOpen] = useState(false);
  const [liveStatus, setLiveStatus] = useState('idle');
  const [liveError, setLiveError] = useState('');
  const [liveDone, setLiveDone] = useState(null);
  const [liveCopied, setLiveCopied] = useState(false);
  const [sunOn, toggleSun] = useSunMode();
  const [carMode, setCarMode] = useState(() => localStorage.getItem('cv_bilan_car_mode') === '1');
  const [collapsedComps, setCollapsedComps] = useState(() => new Set());
  const toggleCarMode = () => setCarMode((v) => { localStorage.setItem('cv_bilan_car_mode', v ? '0' : '1'); return !v; });
  const toggleComp = (id) => setCollapsedComps((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  const type = getBilanType(typeId);
  const isRetour = type.family === 'retour';
  const comps = getBilanCompetences(typeId);
  const total = getBilanTotal(typeId);
  const fiches = (BILAN_FICHES[typeId] || []).map((slug) => FICHES_BY_SLUG[slug]).filter(Boolean);
  const critIds = useMemo(() => new Set(comps.flatMap((c) => c.criteres.map((r) => r.id))), [comps]);

  const setOne = (id, v) => {
    if (v) buzz(v === 'acquis' ? 15 : [12, 60, 12]);
    setGrid((p) => ({ ...p, [id]: v }));
  };
  const toggleHab = (id) => {
    setHabitudes((p) => { if (!p[id]) buzz(15); return { ...p, [id]: !p[id] }; });
  };

  const stats = useMemo(() => {
    const c = { acquis: 0, consolider: 0, non_acquis: 0 };
    Object.entries(grid).forEach(([id, v]) => { if (critIds.has(id) && c[v] !== undefined) c[v]++; });
    return c;
  }, [grid, critIds]);
  const habCount = isRetour ? Object.values(habitudes).filter(Boolean).length : 0;
  const evalCount = stats.acquis + stats.consolider + stats.non_acquis;
  const { kind, pct, text } = evalCount === 0
    ? { kind: 'pending', pct: 0, text: `Évalue les ${total} critères pour obtenir le verdict.` }
    : computeBilanVerdict(typeId, stats, habCount);
  const vs = VERDICT_STYLE[kind] || VERDICT_STYLE.pending;
  const VIcon = vs.Icon;

  const switchType = (id) => {
    if (live) return;
    setTypeId(id);
    setShareResult(null);
  };

  const reset = () => {
    if (!resetConfirm) { setResetConfirm(true); setTimeout(() => setResetConfirm(false), 3000); return; }
    setGrid({}); setHabitudes({}); setConseils('');
    setMeta({ eleve: '', date: todayIso(), km: '', duree: '', rdv: '' });
    setResetConfirm(false);
  };

  const texteParams = () => ({
    typeId,
    eleve: meta.eleve.trim(),
    date: meta.date,
    km: meta.km,
    duree: meta.duree,
    rdv: meta.rdv,
    grid,
    habitudes: isRetour ? habitudes : {},
    verdictText: text,
    pct,
    stats,
    conseils: conseils.trim(),
  });

  const copyTexte = async (mode = 'compact') => {
    try {
      const txt = mode === 'compact' ? buildBilanTexteCompact(texteParams()) : buildBilanTexte(texteParams());
      await navigator.clipboard.writeText(txt);
      setTextCopied({ mode, ...countText(txt) });
      setTimeout(() => setTextCopied(null), 3500);
    } catch { /* silent */ }
  };

  const saveAndShare = async () => {
    if (!meta.eleve.trim()) { setShareError("Renseigne le nom de l'élève avant de partager."); return; }
    if (evalCount === 0) { setShareError('Évalue au moins un critère avant de partager le bilan.'); return; }
    setShareError('');
    setShareStatus('saving');
    try {
      let isoDate;
      const m = (meta.date || '').match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
      if (m) isoDate = `${m[3]}-${m[2]}-${m[1]}`;
      else if (/^\d{4}-\d{2}-\d{2}$/.test(meta.date || '')) isoDate = meta.date;
      const payload = {
        student_name: meta.eleve.trim(),
        date: isoDate,
        bilan_type: typeId,
        parcours_type: type.parcours || 'aac',
        km_parcourus: isRetour && meta.km ? parseInt(meta.km, 10) : null,
        duree_mois: isRetour && meta.duree ? parseInt(meta.duree, 10) : null,
        nb_rdv_peda: isRetour && meta.rdv ? parseInt(meta.rdv, 10) : null,
        grid: Object.fromEntries(Object.entries(grid).filter(([id, v]) => critIds.has(id) && !!v)),
        habitudes: isRetour ? Object.fromEntries(Object.entries(habitudes).filter(([, v]) => !!v)) : {},
        verdict: text,
        verdict_kind: kind,
        score_pct: pct,
        stats: { ...stats, habitudes: habCount, total },
        conseils: conseils.trim() || null,
      };
      const res = await api.createBilanReprise(payload);
      setShareResult({ ...res, fullUrl: `${window.location.origin}/bilan/${res.token}` });
      setShareStatus('done');
    } catch (err) {
      setShareStatus('error');
      setShareError(err?.response?.data?.detail || "Impossible d'enregistrer le bilan.");
    }
  };

  const copyUrl = async () => {
    if (!shareResult?.fullUrl) return;
    try { await navigator.clipboard.writeText(shareResult.fullUrl); setCopied(true); setTimeout(() => setCopied(false), 1800); } catch { /* silent */ }
  };

  // ── BILAN EN DIRECT — partage temps réel avec les parents ──
  const livePayload = () => ({
    student_name: meta.eleve.trim() || undefined,
    grid: Object.fromEntries(Object.entries(grid).filter(([id, v]) => critIds.has(id) && !!v)),
    habitudes: isRetour ? Object.fromEntries(Object.entries(habitudes).filter(([, v]) => !!v)) : {},
    verdict: text,
    verdict_kind: kind === 'pending' ? undefined : kind,
    score_pct: pct,
    stats: { ...stats, habitudes: habCount, total },
    conseils: conseils.trim() || undefined,
  });

  const startLive = async () => {
    if (!meta.eleve.trim()) { setLiveError("Renseigne le nom de l'élève : les parents verront à qui appartient le bilan."); return; }
    setLiveError('');
    setLiveStatus('starting');
    try {
      const res = await api.createBilanLive({
        student_name: meta.eleve.trim(),
        bilan_type: typeId,
        parcours_type: type.parcours || 'aac',
        ...livePayload(),
      });
      setLive({ id: res.id, token: res.token, fullUrl: `${window.location.origin}/bilan/${res.token}` });
      setLiveDone(null);
      setLiveStatus('idle');
      buzz(15);
    } catch (err) {
      setLiveStatus('idle');
      setLiveError(err?.response?.data?.detail || 'Impossible de démarrer le direct.');
    }
  };

  const finalizeLive = async () => {
    if (!live) return;
    setLiveStatus('finalizing');
    try {
      await api.finalizeBilanLive(live.id);
      setLiveDone({ fullUrl: live.fullUrl });
      setLive(null);
      setLiveStatus('idle');
      buzz([15, 60, 15]);
    } catch (err) {
      setLiveStatus('idle');
      setLiveError(err?.response?.data?.detail || 'Impossible de finaliser.');
    }
  };

  // Persistance locale pendant le direct (survit au verrouillage / rechargement du téléphone)
  useEffect(() => {
    if (!live) { localStorage.removeItem(LIVE_KEY); return; }
    try { localStorage.setItem(LIVE_KEY, JSON.stringify({ session: live, typeId, grid, habitudes, meta, conseils })); } catch { /* silent */ }
  }, [live, typeId, grid, habitudes, meta, conseils]);

  // Synchronisation temps réel (debounce 1,2 s) vers les parents
  useEffect(() => {
    if (!live) return undefined;
    const t = setTimeout(async () => {
      try {
        await api.updateBilanLive(live.id, livePayload());
      } catch (err) {
        if (err?.response?.status === 409) { setLiveDone({ fullUrl: live.fullUrl }); setLive(null); }
      }
    }, 1200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live, grid, habitudes, conseils, meta]);

  const liveShareText = () => {
    const url = live?.fullUrl || liveDone?.fullUrl || '';
    return `Bonjour,\n\n${meta.eleve || "Votre élève"} ${live ? 'démarre' : 'a terminé'} son ${type.short.toLowerCase()}.\nSuivez ${live ? "l'évaluation EN DIRECT" : 'le bilan'} ici :\n${url}\n\n${live ? 'La fiche se met à jour automatiquement pendant la leçon.' : 'Le détail complet des critères est maintenant consultable.'}`;
  };
  const copyLiveUrl = async () => {
    const url = live?.fullUrl || liveDone?.fullUrl;
    if (!url) return;
    try { await navigator.clipboard.writeText(url); setLiveCopied(true); setTimeout(() => setLiveCopied(false), 1800); } catch { /* silent */ }
  };

  const buildShareText = () => {
    if (!shareResult) return '';
    return `Bonjour ${meta.eleve},\n\nVoici ton ${type.short.toLowerCase()} :\n${text}\nScore : ${pct}% de critères acquis (${stats.acquis}/${total}).\n\nConsulte le détail ici${isRetour ? ' (à montrer aussi à tes accompagnateurs !)' : ''} :\n${shareResult.fullUrl}\n\nÀ bientôt !`;
  };
  const openSMS = () => { window.location.href = `sms:?body=${encodeURIComponent(buildShareText())}`; };

  const sendToStudentSpace = async () => {
    setInternalSend({ status: 'sending', msg: '' });
    try {
      const d = await api.teacherStudents();
      const target = (d.students || []).find((s) => (s.name || '').trim().toLowerCase() === meta.eleve.trim().toLowerCase());
      if (!target) {
        setInternalSend({ status: 'error', msg: `Aucun élève rattaché nommé « ${meta.eleve} ». Vérifie l'orthographe exacte ou rattache l'élève avec ton code.` });
        return;
      }
      await api.messagesSend(target.id, { body: buildShareText() });
      setInternalSend({ status: 'sent', msg: `Envoyé dans l'espace de ${target.name} ✓` });
    } catch (_) {
      setInternalSend({ status: 'error', msg: 'Envoi impossible — réessaie.' });
    }
  };
  const openWhatsApp = () => { window.open(`https://wa.me/?text=${encodeURIComponent(buildShareText())}`, '_blank'); };
  const openEmail = () => {
    window.location.href = `mailto:?subject=${encodeURIComponent(type.short)}&body=${encodeURIComponent(buildShareText())}`;
  };

  if (!isTeacher) {
    return (
      <div className="max-w-3xl mx-auto p-12 text-center" data-testid="bilan-locked">
        <Lock className="mx-auto text-red-400 mb-4" size={32} />
        <h2 className="font-serif-editorial text-white text-[28px]">Réservé aux enseignants ECSR</h2>
        <p className="text-white/55 mt-2">Les bilans sont un outil d&apos;évaluation exclusivement enseignant.</p>
      </div>
    );
  }

  return (
    <div data-testid="bilan-reprise-tab">
      <BilanPrintSheet
        type={type} comps={comps} grid={grid} habitudes={habitudes} meta={meta}
        stats={stats} total={total} pct={pct} verdictText={text} conseils={conseils.trim()} isRetour={isRetour}
      />
      <div className="print-hide">
      {/* Hero */}
      {!carMode && (
      <div className="rounded-2xl sm:rounded-3xl border border-white/10 bg-gradient-to-br from-[#091509] via-[#0d0d0e] to-[#0d0d0e] p-5 sm:p-7 lg:p-9 mb-6 relative overflow-hidden">
        <div className="pointer-events-none absolute -top-20 -right-20 w-72 h-72 rounded-full bg-emerald-600/12 blur-3xl" />
        <div className="relative z-10">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-emerald-700/40 bg-emerald-600/10 mb-4">
            <ClipboardList size={13} className="text-emerald-400" />
            <span className="text-[10.5px] uppercase tracking-[0.22em] text-emerald-400 font-bold">Évaluation Enseignant · Bilans de formation</span>
          </div>
          <h1 className="font-serif-editorial text-white text-[34px] sm:text-[44px] lg:text-[52px] leading-[1.02] tracking-tight">
            Bilans<br />
            <span className="text-emerald-500">{type.short.toLowerCase()}</span>
          </h1>
          <p className="text-white/70 text-[15px] mt-5 max-w-2xl leading-relaxed">
            Choisis le type de bilan, évalue les <span className="text-emerald-400 font-semibold">{total} critères REMC</span> de la ou des compétences traitées{isRetour ? <>, coche les <span className="text-amber-400 font-semibold">habitudes à corriger</span></> : ''} et partage par QR, SMS, WhatsApp — ou copie le texte pour l&apos;intégrer dans ton outil Codes Rousseau.
          </p>
        </div>
      </div>
      )}

      {/* Barre sticky — progression + Mode Voiture */}
      <div className="sticky top-0 z-30 mb-5 rounded-xl border border-white/10 bg-[#0a0a0b]/95 backdrop-blur-md px-3 sm:px-4 py-2.5 flex items-center gap-2.5 sm:gap-3 no-print shadow-lg shadow-black/40" data-testid="bilan-sticky-bar">
        <span className="font-mono-spec text-[12.5px] text-white/70 shrink-0">{evalCount}<span className="text-white/35">/{total}</span></span>
        <div className="flex-1 h-2 rounded-full bg-white/10 overflow-hidden min-w-[36px]">
          <div className="h-full rounded-full bg-gradient-to-r from-emerald-600 to-emerald-400 transition-all" style={{ width: `${Math.round((evalCount / total) * 100)}%` }} />
        </div>
        <span className={`font-mono-spec text-[13px] font-bold shrink-0 ${kind === 'pret' ? 'text-emerald-400' : kind === 'presque' ? 'text-sky-400' : kind === 'pas_pret' ? 'text-amber-400' : 'text-white/40'}`}>{pct}%</span>
        <button onClick={toggleSun} data-testid="bilan-sun-toggle"
          title={sunOn ? 'Repasser en sombre' : 'Thème clair — lecture plein soleil'}
          className="shrink-0 w-9 h-9 rounded-full border border-white/15 bg-white/5 text-amber-300 hover:bg-white/10 inline-flex items-center justify-center transition">
          {sunOn ? <Moon size={14} /> : <Sun size={14} />}
        </button>
        {!live ? (
          <button onClick={() => { setLiveError(''); setLiveDone(null); setLiveOpen(true); }} data-testid="bilan-live-start-btn"
            title="Partager le bilan en direct avec les parents : la fiche s'agrémente en temps réel pendant l'évaluation"
            className="shrink-0 h-9 px-3 rounded-full border border-red-500/40 bg-red-600/15 text-red-300 hover:bg-red-600/25 text-[11.5px] font-bold inline-flex items-center gap-1.5 transition">
            <Radio size={13} /> Direct
          </button>
        ) : (
          <button onClick={() => { setLiveError(''); setLiveOpen(true); }} data-testid="bilan-live-open-btn"
            className="shrink-0 h-9 px-3 rounded-full bg-red-600 text-white text-[11.5px] font-bold inline-flex items-center gap-1.5 shadow-md shadow-red-900/40">
            <span className="w-2 h-2 rounded-full bg-white animate-pulse" /> LIVE
          </button>
        )}
        <button onClick={toggleCarMode} data-testid="bilan-car-mode-toggle"
          title="Mode voiture : texte agrandi, gros boutons, uniquement l'essentiel pour la leçon"
          className={`shrink-0 h-9 px-3 rounded-full border text-[11.5px] font-bold inline-flex items-center gap-1.5 transition ${carMode ? 'bg-emerald-600 border-emerald-500 text-white shadow-md' : 'border-white/15 bg-white/5 text-white/70 hover:text-white'}`}>
          <Car size={13} /> {carMode ? 'Voiture ON' : 'Mode voiture'}
        </button>
      </div>

      {/* Sélecteur de type de bilan */}
      <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-4 lg:p-5 mb-5" data-testid="bilan-type-selector">
        <div className="text-[10.5px] uppercase tracking-[0.22em] text-emerald-400 font-bold font-mono-spec mb-3">Type de bilan</div>
        <div className="flex flex-wrap gap-2">
          {BILAN_TYPES.map((t) => (
            <button
              key={t.id}
              onClick={() => switchType(t.id)}
              disabled={!!live && typeId !== t.id}
              data-testid={`bilan-type-${t.id}`}
              className={`h-10 px-4 rounded-full border text-[12.5px] font-bold transition disabled:opacity-30 disabled:cursor-not-allowed ${typeId === t.id
                ? (t.family === 'retour' ? 'bg-emerald-600 border-emerald-500 text-white shadow-md' : 'bg-sky-600 border-sky-500 text-white shadow-md')
                : 'border-white/10 bg-white/5 text-white/55 hover:bg-white/10 hover:text-white'}`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <p className="mt-2.5 text-white/45 text-[12px]">{type.short} — {type.desc}</p>
      </div>

      {/* Panneau d'information — RDV Préalable AFFI */}
      {!carMode && type.special === 'rdv_prealable' && (
        <div className="rounded-2xl border border-sky-500/30 bg-gradient-to-br from-sky-950/20 to-[#0d0d0e] p-4 lg:p-6 mb-5" data-testid="bilan-rdv-info">
          <div className="text-[10.5px] uppercase tracking-[0.22em] text-sky-300 font-bold font-mono-spec mb-2">Qu&apos;est-ce que le RDV préalable ?</div>
          <p className="text-white text-[13.5px] font-semibold">{RDV_PREALABLE_INFO.bandeau}</p>
          <ul className="mt-3 space-y-1.5">
            {RDV_PREALABLE_INFO.utilites.map((u, i) => (
              <li key={i} className="flex gap-2.5 text-white/80 text-[13px] leading-snug">
                <span className="shrink-0 mt-1.5 w-1.5 h-1.5 rounded-full bg-sky-400" />
                <span>{u}</span>
              </li>
            ))}
          </ul>
          <p className="mt-3 text-emerald-300/90 text-[12.5px] italic">{RDV_PREALABLE_INFO.rappel}</p>

          <div className="mt-4 grid md:grid-cols-2 gap-3">
            {RDV_PREALABLE_INFO.deroulement.map((d, i) => (
              <div key={i} className="rounded-xl border border-white/10 bg-white/[0.03] p-3.5">
                <div className="text-sky-300 text-[11px] font-bold font-mono-spec uppercase tracking-wider mb-1.5">{i + 1}. {d.titre}</div>
                <p className="text-white/70 text-[12.5px] leading-relaxed">{d.texte}</p>
              </div>
            ))}
          </div>

          {/* Comparatif RDV préalable vs pédagogique */}
          <div className="mt-4 rounded-xl border border-white/10 overflow-hidden">
            <table className="w-full text-[12px]">
              <thead>
                <tr className="bg-white/5 text-left">
                  <th className="px-3 py-2 text-white/45 font-mono-spec uppercase tracking-wider text-[10px]"></th>
                  <th className="px-3 py-2 text-sky-300 font-mono-spec uppercase tracking-wider text-[10px]">RDV Préalable</th>
                  <th className="px-3 py-2 text-white/55 font-mono-spec uppercase tracking-wider text-[10px]">RDV Pédagogique (AAC)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {RDV_PREALABLE_INFO.comparatif.map((r) => (
                  <tr key={r.carac}>
                    <td className="px-3 py-2 text-white/50 font-semibold">{r.carac}</td>
                    <td className="px-3 py-2 text-white/85">{r.prealable}</td>
                    <td className="px-3 py-2 text-white/60">{r.pedagogique}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-4 rounded-xl border border-amber-600/35 bg-amber-950/15 p-3.5">
            <div className="text-amber-300 text-[11px] font-bold font-mono-spec uppercase tracking-wider mb-1.5 inline-flex items-center gap-1.5">
              <AlertTriangle size={12} /> Après le rendez-vous — à ne pas oublier
            </div>
            <ul className="space-y-1.5">
              {RDV_PREALABLE_INFO.apres.map((a, i) => (
                <li key={i} className="flex gap-2.5 text-amber-100/90 text-[12.5px] leading-snug">
                  <span className="shrink-0 mt-0.5 text-amber-400 font-bold">→</span>
                  <span>{a}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-4">
            <RdvPrealableGuide />
          </div>
        </div>
      )}

      {/* Fiches pédagogiques Top 10 pré-requis — supports d'échange avec l'élève */}
      {fiches.length > 0 && (
        <div className="rounded-2xl border border-amber-500/30 bg-gradient-to-br from-amber-950/20 to-[#0d0d0e] p-4 lg:p-5 mb-5" data-testid="bilan-fiches-panel">
          <div className="grid lg:grid-cols-[1fr_auto] gap-4 items-start">
            <div>
              <div className="text-[10.5px] uppercase tracking-[0.22em] text-amber-300 font-bold font-mono-spec mb-2 inline-flex items-center gap-1.5">
                <Presentation size={12} /> Fiches pédagogiques · Top 10 pré-requis
              </div>
              <p className="text-white/75 text-[13px] leading-relaxed max-w-2xl">
                Un critère « non acquis » ou « à consolider » ? Appuie-toi sur les fiches liées à ce bilan : visuel plein écran à montrer à l&apos;élève, puis mini QCM pour valider l&apos;ancrage.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                {fiches.map((f) => (
                  <div key={f.slug} className="inline-flex items-stretch rounded-full border border-amber-500/40 bg-amber-500/10 overflow-hidden" data-testid={`bilan-fiche-${f.slug}`}>
                    <button
                      onClick={() => setPresenting({ item: f, startAt: 'start' })}
                      data-testid={`bilan-fiche-visuel-${f.slug}`}
                      title={`Visuel plein écran — ${f.title}`}
                      className="h-10 pl-4 pr-3 text-amber-100 hover:bg-amber-500/20 text-[12.5px] font-bold inline-flex items-center gap-1.5 transition"
                    >
                      <Maximize2 size={12} /> {f.title}
                    </button>
                    {f.qcm?.length > 0 && (
                      <button
                        onClick={() => setPresenting({ item: f, startAt: 'qcm' })}
                        data-testid={`bilan-fiche-qcm-${f.slug}`}
                        title={`Mini QCM — ${f.title}`}
                        className="h-10 px-3 border-l border-amber-500/30 text-amber-300 hover:bg-amber-500/25 text-[11.5px] font-bold inline-flex items-center gap-1 transition"
                      >
                        <HelpCircle size={12} /> QCM
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
            {fiches.length === 1 && VISUAL_MAP[fiches[0].slug] && (
              <button
                onClick={() => setPresenting({ item: fiches[0], startAt: 'start' })}
                title="Ouvrir le visuel en plein écran"
                className="hidden lg:block w-64 rounded-xl border border-amber-500/30 overflow-hidden hover:border-amber-400/60 transition"
                data-testid="bilan-fiche-thumb"
              >
                {React.createElement(VISUAL_MAP[fiches[0].slug])}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Contexte */}
      <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-4 lg:p-5 mb-5">
        <div className="text-[10.5px] uppercase tracking-[0.22em] text-emerald-400 font-bold font-mono-spec mb-3">{isRetour ? 'Contexte du parcours' : 'Identité'}</div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <label className="flex flex-col gap-1.5">
            <span className="text-[10.5px] uppercase tracking-[0.18em] text-white/45 font-bold font-mono-spec">Élève · Nom / Prénom</span>
            <input value={meta.eleve} onChange={(e) => setMeta({ ...meta, eleve: e.target.value })} data-testid="bilan-meta-eleve" placeholder="Nom Prénom"
              className="h-10 px-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-white/35 text-[13px] focus:outline-none focus:border-emerald-500/50" />
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-[10.5px] uppercase tracking-[0.18em] text-white/45 font-bold font-mono-spec">Date du bilan · auto (aujourd&apos;hui)</span>
            <input type="date" value={meta.date} onChange={(e) => setMeta({ ...meta, date: e.target.value })} data-testid="bilan-meta-date" style={{ colorScheme: 'dark' }}
              className="h-10 px-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-white/35 text-[13px] focus:outline-none focus:border-emerald-500/50" />
          </label>
          {isRetour && (
            <>
              <label className="flex flex-col gap-1.5">
                <span className="text-[10.5px] uppercase tracking-[0.18em] text-white/45 font-bold font-mono-spec">Km parcourus (approx.)</span>
                <input value={meta.km} onChange={(e) => setMeta({ ...meta, km: e.target.value.replace(/\D/g, '') })} data-testid="bilan-meta-km" placeholder="ex : 3000" inputMode="numeric"
                  className="h-10 px-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-white/35 text-[13px] focus:outline-none focus:border-emerald-500/50" />
              </label>
              <label className="flex flex-col gap-1.5">
                <span className="text-[10.5px] uppercase tracking-[0.18em] text-white/45 font-bold font-mono-spec">Durée (mois)</span>
                <input value={meta.duree} onChange={(e) => setMeta({ ...meta, duree: e.target.value.replace(/\D/g, '') })} data-testid="bilan-meta-duree" placeholder="ex : 12" inputMode="numeric"
                  className="h-10 px-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-white/35 text-[13px] focus:outline-none focus:border-emerald-500/50" />
              </label>
              <label className="flex flex-col gap-1.5">
                <span className="text-[10.5px] uppercase tracking-[0.18em] text-white/45 font-bold font-mono-spec">RDV pédagogiques déjà faits</span>
                <input value={meta.rdv} onChange={(e) => setMeta({ ...meta, rdv: e.target.value.replace(/\D/g, '') })} data-testid="bilan-meta-rdv" placeholder="ex : 1" inputMode="numeric"
                  className="h-10 px-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-white/35 text-[13px] focus:outline-none focus:border-emerald-500/50" />
              </label>
            </>
          )}
        </div>
      </div>

      {/* Stats + verdict */}
      {!carMode && (
      <div className={`grid grid-cols-2 ${isRetour ? 'md:grid-cols-5' : 'md:grid-cols-4'} gap-3 mb-4`}>
        <div className="rounded-xl border border-white/10 bg-[#0d0d0e] p-3.5 text-center">
          <div className="text-[10.5px] uppercase tracking-wider text-white/45 font-bold font-mono-spec">Évalué</div>
          <div className="font-serif-editorial text-white text-[26px] leading-none mt-1">{evalCount}<span className="text-white/30 text-[14px]">/{total}</span></div>
        </div>
        <div className="rounded-xl border border-emerald-700/30 bg-emerald-950/15 p-3.5 text-center">
          <div className="text-[10.5px] uppercase tracking-wider text-emerald-300 font-bold font-mono-spec">Acquis</div>
          <div className="font-serif-editorial text-emerald-400 text-[26px] leading-none mt-1">{stats.acquis}</div>
        </div>
        <div className="rounded-xl border border-amber-700/30 bg-amber-950/15 p-3.5 text-center">
          <div className="text-[10.5px] uppercase tracking-wider text-amber-300 font-bold font-mono-spec">À consolider</div>
          <div className="font-serif-editorial text-amber-400 text-[26px] leading-none mt-1">{stats.consolider}</div>
        </div>
        <div className="rounded-xl border border-red-700/30 bg-red-950/15 p-3.5 text-center">
          <div className="text-[10.5px] uppercase tracking-wider text-red-300 font-bold font-mono-spec">Non acquis</div>
          <div className="font-serif-editorial text-red-400 text-[26px] leading-none mt-1">{stats.non_acquis}</div>
        </div>
        {isRetour && (
          <div className="rounded-xl border-2 border-amber-700/50 bg-amber-950/25 p-3.5 text-center">
            <div className="text-[10.5px] uppercase tracking-wider text-amber-300 font-bold font-mono-spec">Habitudes</div>
            <div className="font-serif-editorial text-amber-400 text-[26px] leading-none mt-1">{habCount}</div>
          </div>
        )}
      </div>
      )}

      {/* Verdict bar */}
      <div className={`rounded-xl border-2 bg-gradient-to-br to-transparent p-4 mb-6 ${vs.color}`} data-testid="bilan-verdict">
        <div className="inline-flex items-center gap-2 text-[10.5px] uppercase tracking-[0.18em] font-bold font-mono-spec">
          <VIcon size={12} /> Verdict · {pct} % acquis
        </div>
        <p className="text-white/85 text-[14.5px] mt-2 leading-relaxed font-bold">{text}</p>
      </div>

      {/* Grille par compétence REMC */}
      <div className="space-y-5 mb-6">
        {comps.map((comp) => {
          const compEval = comp.criteres.filter((r) => !!grid[r.id]).length;
          const isCollapsed = collapsedComps.has(comp.id);
          return (
          <div key={comp.id} className="rounded-2xl border border-white/10 bg-[#0d0d0e] overflow-hidden">
            <button type="button" onClick={() => toggleComp(comp.id)} data-testid={`bilan-comp-toggle-${comp.id}`}
              className="w-full text-left px-3.5 sm:px-4 py-3 bg-emerald-600/10 border-b border-emerald-700/30 flex items-center gap-2.5 active:bg-emerald-600/20 transition">
              <div className="flex-1 min-w-0">
                <div className={`text-emerald-300 font-bold font-mono-spec uppercase tracking-wider leading-snug ${carMode ? 'text-[14px]' : 'text-[12.5px]'}`}>{comp.id} · {comp.titre}</div>
                {!carMode && <div className="text-white/45 text-[11.5px] mt-0.5">{comp.sub}</div>}
              </div>
              <span className={`shrink-0 font-mono-spec text-[11.5px] font-bold px-2 py-1 rounded-full border ${compEval === comp.criteres.length ? 'border-emerald-500/50 bg-emerald-600/20 text-emerald-300' : 'border-white/15 bg-white/5 text-white/55'}`}>
                {compEval}/{comp.criteres.length}
              </span>
              <ChevronDown size={17} className={`shrink-0 text-white/50 transition-transform ${isCollapsed ? '' : 'rotate-180'}`} />
            </button>
            {!isCollapsed && (
            <ul className="divide-y divide-white/5">
              {comp.criteres.map((r) => {
                const cur = grid[r.id];
                return (
                  <li key={r.id} className={`px-3.5 sm:px-4 ${carMode ? 'py-4' : 'py-3'} lg:grid lg:grid-cols-[1fr_auto] lg:gap-3 lg:items-center`} data-testid={`bilan-row-${r.id}`}>
                    <div className={`text-white leading-snug ${carMode ? 'text-[16.5px] font-medium' : 'text-[14px] sm:text-[13.5px]'}`}>{r.label}</div>
                    <div className="mt-2.5 lg:mt-0 grid grid-cols-3 gap-1.5 lg:flex lg:items-center">
                      {STATES.map((s) => {
                        const I = s.icon;
                        const active = cur === s.v;
                        return (
                          <button key={s.v} onClick={() => setOne(r.id, active ? null : s.v)} data-testid={`bilan-row-${r.id}-${s.v}`}
                            className={`${carMode ? 'h-12 text-[12px]' : 'h-10 text-[11.5px]'} lg:h-8 px-1 lg:px-2.5 rounded-lg lg:rounded-md border font-bold inline-flex items-center justify-center gap-1 transition ${active ? s.cls + ' shadow-md' : 'border-white/10 bg-white/5 text-white/55 hover:bg-white/10 hover:text-white active:bg-white/15'}`}>
                            <I size={carMode ? 14 : 12} /> {s.l}
                          </button>
                        );
                      })}
                    </div>
                  </li>
                );
              })}
            </ul>
            )}
          </div>
          );
        })}
      </div>

      {/* Éléments à corriger — récap automatique */}
      {(stats.consolider + stats.non_acquis) > 0 && (
        <div className="rounded-2xl border border-red-500/30 bg-red-950/10 overflow-hidden mb-6" data-testid="bilan-elements-corriger">
          <div className="px-4 py-3 bg-red-600/10 border-b border-red-700/30">
            <div className="text-[12.5px] text-red-300 font-bold font-mono-spec uppercase tracking-wider">Éléments à corriger · {stats.consolider + stats.non_acquis} point{stats.consolider + stats.non_acquis > 1 ? 's' : ''}</div>
            <div className="text-white/45 text-[11.5px] mt-0.5">Récapitulatif automatique — à passer en revue avec l&apos;élève{isRetour || type.family === 'rdv' ? ' et l\'accompagnateur' : ''} avant de partager le bilan</div>
          </div>
          <ul className="divide-y divide-white/5">
            {comps.flatMap((comp) =>
              comp.criteres
                .filter((r) => grid[r.id] === 'consolider' || grid[r.id] === 'non_acquis')
                .map((r) => (
                  <li key={r.id} className="px-4 py-2.5 flex items-start gap-3">
                    {grid[r.id] === 'non_acquis'
                      ? <span className="shrink-0 mt-0.5 inline-flex items-center gap-1 h-5 px-1.5 rounded-md bg-red-600 text-white text-[10px] font-bold"><XCircle size={9} /> Non acquis</span>
                      : <span className="shrink-0 mt-0.5 inline-flex items-center gap-1 h-5 px-1.5 rounded-md bg-amber-500 text-amber-950 text-[10px] font-bold"><AlertTriangle size={9} /> À consolider</span>}
                    <span className="text-white/85 text-[13px] leading-snug"><span className="text-white/45 font-mono-spec text-[11px]">{comp.id}</span> · {r.label}</span>
                  </li>
                ))
            )}
          </ul>
        </div>
      )}

      {/* Habitudes du retour — uniquement pour les bilans retour */}
      {isRetour && (
        <div className="rounded-2xl border border-amber-700/30 bg-amber-950/10 overflow-hidden mb-6" data-testid="bilan-habitudes-block">
          <div className="px-4 py-3 bg-amber-600/10 border-b border-amber-700/30">
            <div className="text-[12.5px] text-amber-300 font-bold font-mono-spec uppercase tracking-wider">Habitudes prises avec les accompagnateurs · à corriger</div>
            <div className="text-white/45 text-[11.5px] mt-0.5">Coche celles observées pendant l&apos;évaluation — elles pèsent sur le verdict (max {BILAN_REGLES.pret_max_habitudes} pour être « prêt »)</div>
          </div>
          <ul className="divide-y divide-white/5">
            {BILAN_HABITUDES.map((h) => {
              const on = !!habitudes[h.id];
              return (
                <li key={h.id}>
                  <button onClick={() => toggleHab(h.id)} data-testid={`bilan-habitude-${h.id}`}
                    className={`w-full text-left px-4 py-3 flex items-center gap-3 transition ${on ? 'bg-amber-500/10' : 'hover:bg-white/[0.02]'}`}>
                    <span className={`shrink-0 w-5 h-5 rounded-md border-2 flex items-center justify-center transition ${on ? 'bg-amber-500 border-amber-400' : 'border-white/25'}`}>
                      {on && <CheckCircle2 size={13} className="text-amber-950" />}
                    </span>
                    <span className={`leading-snug ${carMode ? 'text-[16px]' : 'text-[13.5px]'} ${on ? 'text-amber-200 font-semibold' : 'text-white/80'}`}>{h.label}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Règle du verdict */}
      {!carMode && (
      <div className="rounded-xl border border-white/10 bg-[#0d0d0e] p-5 mb-6">
        <div className="text-[10.5px] uppercase tracking-[0.22em] text-emerald-400 font-bold font-mono-spec mb-2">Règle du verdict</div>
        <ul className="space-y-1.5 text-white/75 text-[13.5px]">
          <li>≥ <span className="text-emerald-400 font-bold">{BILAN_REGLES.pret_pct} %</span> acquis, aucun « non acquis »{isRetour ? ` et ≤ ${BILAN_REGLES.pret_max_habitudes} habitudes` : ''} → <span className="text-emerald-400 font-semibold">{isRetour ? 'Prêt pour le permis blanc' : 'Compétence validée'}</span>.</li>
          <li>≥ <span className="text-sky-400 font-bold">{BILAN_REGLES.presque_pct} %</span> acquis et ≤ {BILAN_REGLES.presque_max_non_acquis} non acquis → <span className="text-sky-400 font-semibold">{isRetour ? 'Presque prêt' : 'Presque validée'}</span>.</li>
          <li>Sinon → <span className="text-amber-400 font-semibold">{isRetour ? 'Pas encore prêt' : 'Non validée'}</span> (à retravailler en leçon).</li>
        </ul>
      </div>
      )}

      {/* Conseils */}
      <div className="rounded-xl border border-sky-500/25 bg-sky-950/10 p-5 mb-8">
        <label className="block">
          <div className="text-[10.5px] uppercase tracking-[0.22em] text-sky-300 font-bold font-mono-spec mb-2">Conseils à l&apos;élève{isRetour ? ' et aux accompagnateurs' : ''} (facultatif)</div>
          <textarea value={conseils} onChange={(e) => setConseils(e.target.value)} rows={3} data-testid="bilan-conseils"
            placeholder="Ex : retravailler les contrôles avant chaque changement de direction, s'entraîner aux créneaux côté gauche…"
            className="w-full rounded-lg bg-white/5 border border-white/10 text-white placeholder-white/35 text-[13.5px] p-3 focus:outline-none focus:border-sky-500/50" />
        </label>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-end flex-wrap gap-2 pt-6 border-t border-white/5">
        <button onClick={reset} data-testid="bilan-reset" className={`inline-flex items-center gap-2 h-11 px-4 rounded-full border text-[13px] font-semibold transition ${resetConfirm ? 'bg-red-700 border-red-600 text-white' : 'border-white/15 bg-white/5 hover:bg-white/10 text-white'}`}>
          <RotateCcw size={14} /> {resetConfirm ? 'Confirmer la remise à zéro' : 'Réinitialiser'}
        </button>
        <button onClick={() => window.print()} data-testid="bilan-print" className="inline-flex items-center gap-2 h-11 px-5 rounded-full border border-white/15 bg-white/5 hover:bg-white/10 text-white text-[13px] font-semibold transition">
          <Printer size={14} /> Imprimer / PDF
        </button>
        <button onClick={() => copyTexte('compact')} data-testid="bilan-copy-rousseau" disabled={evalCount === 0}
          title="Version compacte : verdict + points à travailler uniquement — adaptée aux limites de mots des commentaires Codes Rousseau"
          className="inline-flex items-center gap-2 h-11 px-5 rounded-full border border-sky-500/40 bg-sky-600/15 hover:bg-sky-600/25 disabled:opacity-40 disabled:cursor-not-allowed text-sky-200 text-[13px] font-semibold transition">
          {textCopied?.mode === 'compact'
            ? <><CheckCircle2 size={14} className="text-emerald-400" /> Copié · {textCopied.mots} mots / {textCopied.caracteres} car.</>
            : <><ClipboardList size={14} /> Copier compact (Rousseau)</>}
        </button>
        <button onClick={() => copyTexte('complet')} data-testid="bilan-copy-complet" disabled={evalCount === 0}
          title="Version complète : tous les critères détaillés (peut dépasser les limites de l'outil Rousseau)"
          className="inline-flex items-center gap-2 h-11 px-4 rounded-full border border-white/15 bg-white/5 hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed text-white/70 text-[12.5px] font-semibold transition">
          {textCopied?.mode === 'complet'
            ? <><CheckCircle2 size={13} className="text-emerald-400" /> Copié · {textCopied.mots} mots / {textCopied.caracteres} car.</>
            : <><Copy size={13} /> Texte complet</>}
        </button>
        <button onClick={() => { setShareError(''); setShareResult(null); setShareStatus('idle'); setInternalSend({ status: 'idle', msg: '' }); setShareOpen(true); }}
          data-testid="bilan-share-qr" disabled={evalCount === 0 || !meta.eleve.trim()}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-emerald-600 hover:bg-emerald-500 disabled:bg-emerald-900/40 disabled:cursor-not-allowed text-white text-[13px] font-semibold shadow-lg shadow-emerald-900/40 transition">
          <QrCode size={14} /> Envoyer à l&apos;élève (QR)
        </button>
      </div>

      {/* Mode présentation plein écran — fiche Top 10 */}
      {presenting && (
        <PrerequisPresentMode
          item={presenting.item}
          startAt={presenting.startAt}
          onClose={() => setPresenting(null)}
        />
      )}

      {/* Modal Bilan EN DIRECT */}
      {liveOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 no-print" data-testid="bilan-live-modal" onClick={() => setLiveOpen(false)}>
          <div className="bg-[#0d0d0e] border border-white/10 rounded-2xl max-w-md w-full p-6 relative max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setLiveOpen(false)} data-testid="bilan-live-close" className="absolute top-3 right-3 w-8 h-8 rounded-full border border-white/10 bg-white/5 hover:bg-white/10 text-white/70 flex items-center justify-center">
              <X size={14} />
            </button>

            {!live && !liveDone && (
              <>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-red-700/40 bg-red-600/10 mb-4">
                  <Radio size={12} className="text-red-400" />
                  <span className="text-[10.5px] uppercase tracking-[0.22em] text-red-400 font-bold font-mono-spec">Bilan en direct</span>
                </div>
                <h3 className="font-serif-editorial text-white text-[22px] leading-tight">Partager avant la leçon</h3>
                <p className="mt-2 text-white/65 text-[13px] leading-relaxed">
                  Envoie ce lien aux parents <strong className="text-white">avant de démarrer</strong> : ils verront la
                  <strong className="text-white"> progression globale en temps réel</strong> pendant que tu évalues (compteurs et avancement — pas le détail).
                  Le détail complet des critères apparaîtra à la <strong className="text-white">finalisation</strong>.
                </p>
                <ul className="mt-3 space-y-1.5 text-white/60 text-[12.5px]">
                  <li className="flex gap-2"><span className="text-red-400">•</span> Chaque case cochée met la fiche à jour chez les parents (~3 s)</li>
                  <li className="flex gap-2"><span className="text-red-400">•</span> Finalise en fin de leçon pour révéler le bilan complet</li>
                  <li className="flex gap-2"><span className="text-red-400">•</span> Auto-finalisation après 4 h si tu oublies</li>
                </ul>
                <label className="mt-4 flex flex-col gap-1.5">
                  <span className="text-[10.5px] uppercase tracking-[0.18em] text-white/45 font-bold font-mono-spec">Élève · Nom / Prénom</span>
                  <input value={meta.eleve} onChange={(e) => setMeta({ ...meta, eleve: e.target.value })} data-testid="bilan-live-eleve" placeholder="Nom Prénom"
                    className="h-11 px-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-white/35 text-[14px] focus:outline-none focus:border-red-500/50" />
                </label>
                {liveError && <div className="mt-3 rounded-md border border-red-500/40 bg-red-950/30 px-3 py-2 text-red-300 text-[12.5px]" data-testid="bilan-live-error">{liveError}</div>}
                <div className="mt-5 flex items-center justify-end gap-2">
                  <button onClick={() => setLiveOpen(false)} className="h-10 px-4 rounded-full border border-white/15 text-white/80 text-[12.5px] font-semibold">Annuler</button>
                  <button onClick={startLive} disabled={liveStatus === 'starting'} data-testid="bilan-live-start"
                    className="h-10 px-5 rounded-full bg-red-600 hover:bg-red-500 disabled:bg-red-900/40 text-white text-[12.5px] font-bold inline-flex items-center gap-1.5">
                    {liveStatus === 'starting' ? (<><Loader2 size={13} className="animate-spin" /> Démarrage…</>) : (<><Radio size={13} /> Démarrer le direct</>)}
                  </button>
                </div>
              </>
            )}

            {live && (
              <>
                <div className="flex items-center gap-2 mb-3">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-red-600 text-white text-[10.5px] uppercase tracking-[0.18em] font-bold font-mono-spec">
                    <span className="w-2 h-2 rounded-full bg-white animate-pulse" /> En direct
                  </span>
                  <span className="text-white/45 text-[11px]">auto-finalisation après 4 h</span>
                </div>
                <h3 className="font-serif-editorial text-white text-[22px] leading-tight">{meta.eleve || "L'élève"} · évaluation en cours</h3>
                <p className="mt-1.5 text-white/65 text-[13px]">Fais scanner ce QR aux parents — la fiche s&apos;agrémente au fur et à mesure de tes coches.</p>
                <div className="mt-4 flex flex-col items-center gap-2">
                  <div className="bg-white p-4 rounded-2xl shadow-[0_0_40px_-8px_rgba(220,38,38,0.4)]" data-testid="bilan-live-qr">
                    <QRCodeSVG value={live.fullUrl} size={190} level="M" />
                  </div>
                </div>
                <div className="mt-4 flex items-center gap-2 bg-[#0a0a0b] border border-white/15 rounded-xl px-3 py-2.5">
                  <span className="flex-1 text-left text-white text-[12px] font-mono-spec break-all">{live.fullUrl}</span>
                  <button onClick={copyLiveUrl} data-testid="bilan-live-copy-url" className="shrink-0 h-8 px-2.5 rounded-full border border-white/15 text-white/85 text-[11.5px] font-semibold inline-flex items-center gap-1">
                    {liveCopied ? <><CheckCircle2 size={11} className="text-emerald-400" /> Copié</> : <><Copy size={11} /> Copier</>}
                  </button>
                </div>
                <div className="mt-3 grid grid-cols-3 gap-2">
                  <button onClick={() => { window.location.href = `sms:?body=${encodeURIComponent(liveShareText())}`; }} data-testid="bilan-live-sms" className="h-10 px-2 rounded-full bg-red-600 hover:bg-red-500 text-white text-[11.5px] font-bold inline-flex items-center justify-center gap-1"><MessageCircle size={12} /> SMS</button>
                  <button onClick={() => { window.open(`https://wa.me/?text=${encodeURIComponent(liveShareText())}`, '_blank'); }} data-testid="bilan-live-whatsapp" className="h-10 px-2 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white text-[11.5px] font-bold inline-flex items-center justify-center gap-1"><Send size={12} /> WhatsApp</button>
                  <button onClick={() => { window.location.href = `mailto:?subject=${encodeURIComponent(`${type.short} en direct`)}&body=${encodeURIComponent(liveShareText())}`; }} data-testid="bilan-live-email" className="h-10 px-2 rounded-full bg-sky-600 hover:bg-sky-500 text-white text-[11.5px] font-bold inline-flex items-center justify-center gap-1"><Mail size={12} /> Email</button>
                </div>
                {liveError && <div className="mt-3 rounded-md border border-red-500/40 bg-red-950/30 px-3 py-2 text-red-300 text-[12.5px]">{liveError}</div>}
                <button onClick={finalizeLive} disabled={liveStatus === 'finalizing'} data-testid="bilan-live-finalize"
                  className="mt-4 w-full h-12 rounded-full bg-emerald-600 hover:bg-emerald-500 disabled:bg-emerald-900/40 text-white text-[13.5px] font-bold inline-flex items-center justify-center gap-2 shadow-lg shadow-emerald-900/40">
                  {liveStatus === 'finalizing' ? (<><Loader2 size={14} className="animate-spin" /> Finalisation…</>) : (<><Flag size={14} /> Finaliser le bilan</>)}
                </button>
                <p className="mt-2 text-center text-white/40 text-[11px]">La finalisation révèle le détail complet aux parents — le lien reste le même.</p>
              </>
            )}

            {!live && liveDone && (
              <>
                <div className="text-center">
                  <CheckCircle2 size={40} className="text-emerald-400 mx-auto mb-3" />
                  <h3 className="font-serif-editorial text-white text-[22px]">Bilan finalisé ✓</h3>
                  <p className="mt-1.5 text-white/65 text-[13px]">Les parents voient maintenant le <strong className="text-white">détail complet</strong> sur le même lien.</p>
                </div>
                <div className="mt-5 flex flex-col items-center gap-2">
                  <div className="bg-white p-4 rounded-2xl shadow-[0_0_40px_-8px_rgba(16,185,129,0.4)]" data-testid="bilan-live-done-qr">
                    <QRCodeSVG value={liveDone.fullUrl} size={180} level="M" />
                  </div>
                </div>
                <div className="mt-4 flex items-center gap-2 bg-[#0a0a0b] border border-white/15 rounded-xl px-3 py-2.5">
                  <span className="flex-1 text-left text-white text-[12px] font-mono-spec break-all">{liveDone.fullUrl}</span>
                  <button onClick={copyLiveUrl} data-testid="bilan-live-done-copy" className="shrink-0 h-8 px-2.5 rounded-full border border-white/15 text-white/85 text-[11.5px] font-semibold inline-flex items-center gap-1">
                    {liveCopied ? <><CheckCircle2 size={11} className="text-emerald-400" /> Copié</> : <><Copy size={11} /> Copier</>}
                  </button>
                </div>
                <button onClick={() => setLiveOpen(false)} className="mt-4 w-full h-10 rounded-full border border-white/15 text-white/80 text-[12.5px] font-semibold">Fermer</button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Modal de partage */}
      {shareOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 no-print" data-testid="bilan-share-modal" onClick={() => setShareOpen(false)}>
          <div className="bg-[#0d0d0e] border border-white/10 rounded-2xl max-w-md w-full p-6 relative max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setShareOpen(false)} data-testid="bilan-share-close" className="absolute top-3 right-3 w-8 h-8 rounded-full border border-white/10 bg-white/5 hover:bg-white/10 text-white/70 flex items-center justify-center">
              <X size={14} />
            </button>
            {shareStatus !== 'done' && (
              <>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-emerald-700/40 bg-emerald-600/10 mb-4">
                  <Share2 size={12} className="text-emerald-400" />
                  <span className="text-[10.5px] uppercase tracking-[0.22em] text-emerald-400 font-bold font-mono-spec">Partager le bilan</span>
                </div>
                <h3 className="font-serif-editorial text-white text-[22px] leading-tight">{type.short} · {meta.eleve || "l'élève"}</h3>
                <p className="mt-2 text-white/65 text-[13px] leading-relaxed">
                  L&apos;élève{isRetour ? <> <strong className="text-white">et ses accompagnateurs</strong></> : ''} pourront consulter le verdict et le détail des critères — sans créer de compte.
                </p>
                <div className="mt-4 rounded-lg border border-white/10 bg-white/[0.03] p-3 text-[12.5px] text-white/70 space-y-1">
                  <div><span className="text-white/45">Type :</span> <span className="text-white font-semibold">{type.short}</span></div>
                  <div><span className="text-white/45">Score :</span> <span className="text-white font-semibold">{pct}% acquis ({stats.acquis}/{total})</span></div>
                  <div><span className="text-white/45">Verdict :</span> <span className="text-white font-semibold">{text}</span></div>
                </div>
                {shareError && <div className="mt-3 rounded-md border border-red-500/40 bg-red-950/30 px-3 py-2 text-red-300 text-[12.5px]" data-testid="bilan-share-error">{shareError}</div>}
                <div className="mt-5 flex items-center justify-end gap-2">
                  <button onClick={() => setShareOpen(false)} className="h-10 px-4 rounded-full border border-white/15 text-white/80 text-[12.5px] font-semibold">Annuler</button>
                  <button onClick={saveAndShare} disabled={shareStatus === 'saving'} data-testid="bilan-share-save"
                    className="h-10 px-5 rounded-full bg-emerald-600 hover:bg-emerald-500 disabled:bg-emerald-900/40 text-white text-[12.5px] font-bold inline-flex items-center gap-1.5">
                    {shareStatus === 'saving' ? (<><Loader2 size={13} className="animate-spin" /> Enregistrement…</>) : (<><QrCode size={13} /> Générer QR Code</>)}
                  </button>
                </div>
              </>
            )}
            {shareStatus === 'done' && shareResult && (
              <>
                <div className="text-center">
                  <CheckCircle2 size={40} className="text-emerald-400 mx-auto mb-3" />
                  <h3 className="font-serif-editorial text-white text-[22px]">Bilan enregistré ✓</h3>
                  <p className="mt-1.5 text-white/65 text-[13px]">Fais scanner ce QR code à <strong className="text-white">{meta.eleve}</strong>{isRetour ? ' (et à ses accompagnateurs)' : ''}.</p>
                </div>
                <div className="mt-5 flex flex-col items-center gap-2">
                  <div className="bg-white p-4 rounded-2xl shadow-[0_0_40px_-8px_rgba(16,185,129,0.4)]" data-testid="bilan-qr-code">
                    <QRCodeSVG value={shareResult.fullUrl} size={200} level="M" />
                  </div>
                </div>
                <div className="mt-4 flex items-center gap-2 bg-[#0a0a0b] border border-white/15 rounded-xl px-3 py-2.5">
                  <span className="flex-1 text-left text-white text-[12px] font-mono-spec break-all">{shareResult.fullUrl}</span>
                  <button onClick={copyUrl} data-testid="bilan-copy-url" className="shrink-0 h-8 px-2.5 rounded-full border border-white/15 text-white/85 text-[11.5px] font-semibold inline-flex items-center gap-1">
                    {copied ? <><CheckCircle2 size={11} className="text-emerald-400" /> Copié</> : <><Copy size={11} /> Copier</>}
                  </button>
                </div>
                <div className="mt-3 grid grid-cols-3 gap-2">
                  <button onClick={openSMS} data-testid="bilan-share-sms" className="h-10 px-2 rounded-full bg-red-600 hover:bg-red-500 text-white text-[11.5px] font-bold inline-flex items-center justify-center gap-1"><MessageCircle size={12} /> SMS</button>
                  <button onClick={openWhatsApp} data-testid="bilan-share-whatsapp" className="h-10 px-2 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white text-[11.5px] font-bold inline-flex items-center justify-center gap-1"><Send size={12} /> WhatsApp</button>
                  <button onClick={openEmail} data-testid="bilan-share-email" className="h-10 px-2 rounded-full bg-sky-600 hover:bg-sky-500 text-white text-[11.5px] font-bold inline-flex items-center justify-center gap-1"><Mail size={12} /> Email</button>
                </div>
                <button onClick={sendToStudentSpace} disabled={internalSend.status === 'sending' || internalSend.status === 'sent'} data-testid="bilan-send-internal"
                  className={`mt-2 w-full h-10 rounded-full text-[12px] font-bold inline-flex items-center justify-center gap-1.5 ${internalSend.status === 'sent' ? 'bg-emerald-600/25 border border-emerald-500/50 text-emerald-300' : 'bg-amber-500 hover:bg-amber-400 text-black'}`}>
                  {internalSend.status === 'sending' ? (<><Loader2 size={12} className="animate-spin" /> Envoi…</>)
                    : internalSend.status === 'sent' ? (<><CheckCircle2 size={12} /> {internalSend.msg}</>)
                    : (<><Send size={12} /> Envoyer dans l'espace élève (messagerie)</>)}
                </button>
                {internalSend.status === 'error' && <p className="mt-1.5 text-red-300 text-[11.5px] text-center" data-testid="bilan-send-internal-error">⚠️ {internalSend.msg}</p>}
                <button onClick={() => copyTexte('compact')} data-testid="bilan-modal-copy-rousseau"
                  className="mt-3 w-full h-10 rounded-full border border-sky-500/40 bg-sky-600/15 hover:bg-sky-600/25 text-sky-200 text-[12px] font-semibold inline-flex items-center justify-center gap-1.5">
                  {textCopied?.mode === 'compact'
                    ? <><CheckCircle2 size={12} className="text-emerald-400" /> Copié · {textCopied.mots} mots / {textCopied.caracteres} car.</>
                    : <><ClipboardList size={12} /> Copier compact (outil Rousseau)</>}
                </button>
              </>
            )}
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
